// Runtime configuration, fetched from `config.json` next to the assets, so one build runs everywhere.
export interface RuntimeConfig {
  /** OIDC issuer, e.g. https://auth.alturos.com/auth/realms/corp */
  issuer: string;
  /** Public client (authorization code + PKCE). */
  clientId: string;
  /** Requested audience; sent as the `audience` parameter only when set (Keycloak maps audiences on the client). */
  audience?: string;
  scope?: string;
  /** REST base, default `/api/v1` (absolute path or URL). */
  apiBase?: string;
  /** Live-log WebSocket base; default: apiBase on the same host with ws(s)://. */
  logsBase?: string;
}

export async function loadConfig(fetchImpl: typeof fetch = fetch): Promise<Required<Omit<RuntimeConfig, 'audience'>> & Pick<RuntimeConfig, 'audience'>> {
  const res = await fetchImpl(new URL('config.json', document.baseURI).toString(), { cache: 'no-store' });
  if (!res.ok) throw new Error(`config.json could not be loaded (HTTP ${res.status}).`);
  const raw = (await res.json()) as Partial<RuntimeConfig>;
  for (const key of ['issuer', 'clientId'] as const) {
    if (typeof raw[key] !== 'string' || raw[key] === '') throw new Error(`config.json lacks "${key}".`);
  }
  const apiBase = (raw.apiBase ?? '/api/v1').replace(/\/+$/, '');
  return {
    issuer: raw.issuer!,
    clientId: raw.clientId!,
    audience: raw.audience,
    scope: raw.scope ?? 'openid profile email',
    apiBase,
    logsBase: (raw.logsBase ?? toWs(new URL(apiBase, document.baseURI).toString())).replace(/\/+$/, ''),
  };
}

export function toWs(httpUrl: string): string {
  return httpUrl.replace(/^http(s?):/, 'ws$1:');
}

export type Config = Awaited<ReturnType<typeof loadConfig>>;
